import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Menu, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/api';
import Loading from '../../../components/loading';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';


function TermsTabList() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [termTemplates, setTermTemplates] = useState([]);
  const [selectedTermTemplates, setSelectedTermTemplates] = useState('');
  const [generatedTerms, setGeneratedTerms] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGeneratedTerm, setSelectedGeneratedTerm] = useState(null);
  const openMenu = Boolean(anchorEl);


  const getTermTemplates = useCallback(() => {
    setLoading(true);
    api.get("/terms", {
      headers: { Authorization: sessionStorage.getItem('token') }
    })
      .then((returnFromApi) => {
        setTermTemplates(returnFromApi.data);
        setLoading(false);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response.data);
        console.error("ops! ocorreu um erro" + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate("/expired-session");
        }
      });
  }, [navigate]);

  const getGeneratedTerms = useCallback(() => {
    if (!patientId) return;

    setLoading(true);

    api.get(`/patients/${patientId}/terms`, {
      headers: { Authorization: sessionStorage.getItem('token') }
    }).then((returnFromApi) => {
      setGeneratedTerms(returnFromApi.data);
      setLoading(false);
    }).catch((returnFromApi) => {
      setLoading(false);
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response.data);
      console.error("ops! ocorreu um erro" + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate("/expired-session");
      }
    });
  }, [navigate, patientId]);

  useEffect(() => {
    getTermTemplates();
    getGeneratedTerms();
  }, [getTermTemplates, getGeneratedTerms]);

  const menuOpen = (event, termoGerado) => {
    setAnchorEl(event.currentTarget);
    setSelectedGeneratedTerm(termoGerado);
  };

  const menuClose = () => {
    setAnchorEl(null);
    setSelectedGeneratedTerm(null);
  };

  const generateTerm = () => {
    if (!selectedTermTemplates) {
      setErrorMessage("Selecione um modelo de termo.");
      setShowErrorMessage(true);
      return;
    }
    setLoading(true);

    api.post(`/patients/${patientId}/terms/${selectedTermTemplates}/generate`, null, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setLoading(false);
      setSuccessMessage(returnFromApi.data);
      setShowSuccessMessage(true);
      setSelectedTermTemplates('');
      getGeneratedTerms();
    }).catch((returnFromApi) => {
      setLoading(false);
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response.data);
      console.error("ops! ocorreu um erro" + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate("/expired-session");
      }
    });
  };


  const deleteTerm = () => {
    if (!selectedGeneratedTerm) return;
    const goToDeleteId = selectedGeneratedTerm.id;
    menuClose();

    setLoading(true);

    api.delete(`/patients/${patientId}/terms-generated/${goToDeleteId}`, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    })
      .then((returnFromApi) => {
        setLoading(false);
        setSuccessMessage("Termo excluído com sucesso!");
        setShowSuccessMessage(true);
        getGeneratedTerms();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response.data);
        console.error("ops! ocorreu um erro" + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate("/expired-session");
        }
      });
  };


  const download = () => {
    if (!selectedGeneratedTerm) return;
    const term = selectedGeneratedTerm;
    menuClose();

    setLoading(true);

    api.get(`/patients/${patientId}/terms/${term.id}`, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      const url = returnFromApi.data;
      window.open(url, "_blank");
      setLoading(false);
    }).catch((returnFromApi) => {
      console.error(returnFromApi);
      setLoading(false);
      setErrorMessage(returnFromApi.response.data);
      setShowErrorMessage(true);
    });
  };

  return (
    <Box sx={{ padding: 2 }}>

      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
      {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

      <Paper sx={{ padding: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Gerar Novo Termo
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl fullWidth>
            <InputLabel id="select-termo-template-label">Selecione o modelo</InputLabel>
            <Select
              labelId="select-termo-template-label"
              id="select-termo-template"
              value={selectedTermTemplates}
              label="Selecione o modelo"
              onChange={(e) => setSelectedTermTemplates(e.target.value)}
            >
              <MenuItem value="">
                <em>Nenhum</em>
              </MenuItem>
              {termTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={generateTerm}
            disabled={!selectedTermTemplates || isLoading}
            sx={{ flexShrink: 0, height: '56px' }}
          >
            Gerar Termo
          </Button>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
        Termos Gerados do Paciente
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Geração</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {generatedTerms.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  Nenhum termo foi gerado para este paciente.
                </TableCell>
              </TableRow>
            ) : (
              generatedTerms.map((term) => (
                <TableRow key={term.id} hover>
                  <TableCell>{term.description}</TableCell>
                  <TableCell>
                    {term.createdAt}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="ações"
                      onClick={(event) => menuOpen(event, term)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        id="actions-menu-gerado"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={menuClose}
      >
        <MenuItem onClick={download}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Baixar
        </MenuItem>
        <MenuItem onClick={deleteTerm} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default TermsTabList;